export type MessageLinkSegment =
  | { kind: 'text'; text: string }
  | { kind: 'link'; label: string; url: string }

const LINK_TOKEN_RE = /\[([^\]\r\n]+)\]\((https?:\/\/[^\s)]+)\)|(?<!\]\()((?:https?:\/\/|www\.)[^\s<>\[\]{}()]+|(?:[a-z0-9-]+\.)+(?:com|net|org|io|ai|kr)(?:\/[^\s<>\[\]{}()]*)?(?:\?[^\s<>\[\]{}()]*)?)/gi
const TRAILING_PUNCTUATION_RE = /[.,!;:]$/

function addText(segments: MessageLinkSegment[], text: string) {
  if (!text) return
  const previous = segments[segments.length - 1]
  if (previous?.kind === 'text') {
    previous.text += text
    return
  }
  segments.push({ kind: 'text', text })
}

function safeUrl(url: string): string {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

function splitTrailingPunctuation(value: string): [string, string] {
  let url = value
  let suffix = ''
  while (url && TRAILING_PUNCTUATION_RE.test(url)) {
    suffix = url.slice(-1) + suffix
    url = url.slice(0, -1)
  }
  return [url, suffix]
}

function addStreamingMarkdownTail(
  segments: MessageLinkSegment[],
  tail: string,
): boolean {
  const match = tail.match(/^(.*)\[([^\]\r\n]+)\]\(([^)\s]*)$/s)
  if (!match) return false

  const [, prefix, label, destination] = match
  const isHttpDestination = destination.startsWith('http://')
    || destination.startsWith('https://')
    || 'http://'.startsWith(destination)
    || 'https://'.startsWith(destination)
  if (!isHttpDestination) return false

  addText(segments, prefix)
  addText(segments, label)
  return true
}

export function parseMessageLinks(text: string): MessageLinkSegment[] {
  if (!text) return []

  const segments: MessageLinkSegment[] = []
  let cursor = 0
  LINK_TOKEN_RE.lastIndex = 0

  for (const match of text.matchAll(LINK_TOKEN_RE)) {
    const index = match.index ?? 0
    addText(segments, text.slice(cursor, index))

    const markdownLabel = match[1]
    const markdownUrl = match[2]
    const rawUrl = match[3]
    if (markdownLabel && markdownUrl) {
      segments.push({ kind: 'link', label: markdownLabel, url: markdownUrl })
      cursor = index + match[0].length
      continue
    }

    const [url, suffix] = splitTrailingPunctuation(rawUrl)
    if (url) {
      segments.push({ kind: 'link', label: '링크', url: safeUrl(url) })
    }
    addText(segments, suffix)
    cursor = index + match[0].length
  }

  const tail = text.slice(cursor)
  if (!addStreamingMarkdownTail(segments, tail)) {
    addText(segments, tail)
  }
  return segments
}
